import React from "react";
import { Box, Checkbox, Chip, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import { capitalize, getItemBasePrice, getVariantOptionSurchargeForDisplay, getVariantTotal } from "../../common/util";
import Images from "../../common/component/image/Images";
import currency from "currency.js";

export default function ProductVariants(props) {
  const { fields, variant } = props;

  const sortBySurcharge = (o1, o2) => {
    const amt1 = o1.pricing?.amount || o1.pricing?.factor || 0;
    const amt2 = o2.pricing?.amount || o2.pricing?.factor || 0;
    let rslt = +amt1 - +amt2;
    if (rslt === 0) {
      rslt = o1.name.localeCompare(o2.name);
    }

    return rslt;
  };

  const sortVariants = ([k1, v1], [k2, v2]) => {
    return v2.rank - v1.rank || k2.localeCompare(k1);
  };

  const findOptionByName = (name, arr) => {
    return arr.filter((o) => o.name === name)[0] || "";
  };

  const basePrice = props.basePrice || getItemBasePrice(variant);

  return (
    <Box>
      <Grid container spacing={3}>
        {Object.entries(fields)
          .sort(sortVariants)
          .map(([fieldKey, fieldConfig]) => {
            if (fieldConfig.field.type === "text") {
              return (
                <Grid key={fieldKey} item sm={12} xs={12}>
                  <TextField
                    id={fieldKey}
                    value={variant?.[fieldKey]?.name}
                    required={fieldConfig.required}
                    label={fieldConfig.name}
                    name={fieldKey}
                    variant="outlined"
                    inputProps={{ maxLength: fieldConfig.field?.max }}
                    fullWidth
                    multiline={fieldConfig.field?.max > 40}
                    rows={Math.floor(fieldConfig.field?.max / 40)}
                    onChange={(e) => {
                      props.onChange(fieldKey, { name: e.target.value, rank: fieldConfig.rank || 1 });
                    }}
                    helperText={fieldConfig.description}
                  />
                </Grid>
              );
            } else if (fieldConfig.field.type === "photo") {
              let surcharge = getVariantOptionSurchargeForDisplay(fieldConfig, basePrice);

              return (
                <Grid key={fieldKey} item sm={12} xs={12}>
                  <Box sx={{ border: "1px solid #CCC" }}>
                    <Grid container spacing={2}>
                      <Grid item sm={4} xs={12}>
                        <Images
                          pics={variant?.[fieldKey]?.value || []}
                          placeholderImg={props.placeholderImg}
                          edit={true}
                          onChange={(arr) =>
                            props.onChange(fieldKey, {
                              name: `${arr.length} photos`,
                              rank: fieldConfig.rank || 1,
                              pricing: fieldConfig.pricing,
                              value: arr
                            })
                          }
                          folder={`img/r`}
                          max={fieldConfig.field?.max || 1}
                        />
                      </Grid>
                      <Grid item sm={8} xs={12}>
                        <Box pt={2}>
                          <Typography variant="body1" gutterBottom>
                            Upload Photos {surcharge && `(+${currency(surcharge)})`}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            For best results, use high-resolution photos in JPEG or PNG format. We will crop the image to achieve the best possible
                            fit, but we will not alter the design of the uploaded photos.
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              );
            } else if (fieldConfig.field.type === "select") {
              const isMultiSelect = +fieldConfig.field?.max > 1;

              if (fieldKey === "size" && fieldConfig.field.options.length === 1) {
                return <Box></Box>;
              } else {
                // convert value to string
                let variantValue = "";
                if (isMultiSelect) {
                  variantValue = variant?.[fieldKey]?.value?.map((o) => o.name) || [];
                } else {
                  variantValue = variant?.[fieldKey]?.value?.name || "";
                }

                return (
                  <Grid key={fieldKey} item sm={12} xs={12}>
                    <FormControl variant="outlined" fullWidth required={fieldConfig.required}>
                      <InputLabel id={`${fieldKey}-label`}>{capitalize(fieldConfig.name)}</InputLabel>
                      <Select
                        value={variantValue}
                        required={fieldConfig.required}
                        labelId={`${fieldKey}-label`}
                        label={capitalize(fieldKey)}
                        id={fieldKey}
                        multiple={isMultiSelect}
                        renderValue={(selected) => {
                          if (isMultiSelect) {
                            return (
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {selected?.map((o, idx) => {
                                  const variantValueObj = findOptionByName(o, fieldConfig.field.options);
                                  let surcharge = getVariantOptionSurchargeForDisplay(variantValueObj, basePrice);

                                  return <Chip key={idx} label={`${variantValueObj.name} ${surcharge && ` (+${surcharge})`}`} />;
                                })}
                              </Box>
                            );
                          } else {
                            const variantValueObj = findOptionByName(selected, fieldConfig.field.options);

                            let surcharge = getVariantOptionSurchargeForDisplay(variantValueObj, basePrice);
                            return `${variantValueObj.name} ${surcharge && ` (+${surcharge})`}`;
                          }
                        }}
                        onChange={(e) => {
                          if (Array.isArray(e.target.value)) {
                            let variantValueArr = e.target.value.map((o) => findOptionByName(o, fieldConfig.field.options));

                            props.onChange(fieldKey, {
                              name: variantValueArr.map((o) => o.name).join(", "),
                              rank: fieldConfig.rank || 1,
                              pricing: { amount: getVariantTotal(variantValueArr, basePrice) },
                              value: variantValueArr
                            });
                          } else {
                            const variantValueObj = findOptionByName(e.target.value, fieldConfig.field.options);
                            props.onChange(fieldKey, { ...variantValueObj, value: variantValueObj });
                          }
                        }}
                      >
                        {fieldConfig.field.options.sort(sortBySurcharge).map((option, idx) => {
                          if (isMultiSelect) {
                            let isSelected = false;
                            if (variant?.[fieldKey]?.value?.filter((o) => o.name === option.name)?.length > 0) {
                              isSelected = true;
                            }
                            let surcharge = getVariantOptionSurchargeForDisplay(option, basePrice);

                            return (
                              <MenuItem
                                key={idx}
                                value={option.name}
                                style={{ whiteSpace: "normal" }}
                                disabled={!isSelected && variant?.[fieldKey]?.value.length >= +fieldConfig.field?.max}
                              >
                                <Checkbox checked={isSelected} />
                                <Box>
                                  <Typography variant="body1">
                                    {option.name} {surcharge && ` (+${surcharge})`}
                                  </Typography>
                                  {option.description && (
                                    <Typography variant="body2" color="textSecondary">
                                      {option.description}
                                    </Typography>
                                  )}
                                </Box>
                              </MenuItem>
                            );
                          } else {
                            let surcharge = getVariantOptionSurchargeForDisplay(option, basePrice);

                            return (
                              <MenuItem key={idx} value={option.name} style={{ whiteSpace: "normal" }}>
                                <Box>
                                  <Typography variant="body1">
                                    {fieldKey !== "size" && surcharge && "⭐ "} {option.name} {surcharge && ` (+${surcharge})`}
                                  </Typography>
                                  <Typography variant="body2" color="textSecondary">
                                    {option.description}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            );
                          }
                        })}
                      </Select>
                      <FormHelperText>{fieldConfig.description}</FormHelperText>
                    </FormControl>
                  </Grid>
                );
              }
            }
          })}
      </Grid>
    </Box>
  );
}
